import React, { useState } from 'react';
import { X, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { StatusBadge, TreatmentPill } from './Badges';
import { getPatientStatus } from '../utils/data';

export default function PatientDetailPanel({ patient, onClose, onEdit, onLogVisit }) {
  const [logNote, setLogNote] = useState('');
  const [showLog, setShowLog] = useState(false);
  const status = getPatientStatus(patient);

  function handleLogVisit() {
    if (!logNote.trim()) return;
    onLogVisit(patient.id, logNote);
    setLogNote('');
    setShowLog(false);
  }

  return (
    <div style={overlayStyle} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={panelStyle}>
        <div style={headerStyle}>
          <div>
            <div style={nameStyle}>{patient.firstName} {patient.lastName}</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
              <StatusBadge status={status} />
              <TreatmentPill type={patient.treatmentType} />
            </div>
          </div>
          <button onClick={onClose} style={closeBtnStyle}><X size={18} /></button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, padding: '24px 28px' }}>
          <Section title="Patient details">
            <Grid2>
              <Field label="Date of birth" value={patient.dob ? format(new Date(patient.dob), 'dd MMM yyyy') : '—'} />
              <Field label="Phone" value={patient.phone || '—'} />
              <Field label="Email" value={patient.email || '—'} />
              <Field label="Last visit" value={patient.lastVisit ? format(new Date(patient.lastVisit), 'dd MMM yyyy') : '—'} />
              <Field label="Recall due" value={patient.recallDate ? format(new Date(patient.recallDate), 'dd MMM yyyy') : '—'} />
              <Field 
  label="Personalized Line" 
  value={patient.personalizedLine || 'Not set'} 
/>
              <Field label="Last contacted" value={patient.lastContacted ? format(new Date(patient.lastContacted), 'dd MMM yyyy') : 'Not yet'} />
            </Grid2>
            {patient.notes && (
              <div style={{ marginTop: 12 }}>
                <Field label="Notes" value={patient.notes} />
              </div>
            )}
          </Section>

          <Section title="Visit history">
            {(!patient.visitHistory || patient.visitHistory.length === 0) ? (
              <p style={emptyTextStyle}>No visits logged yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {[...patient.visitHistory].reverse().map((v, i) => (
                  <div key={i} style={timelineItemStyle}>
                    <div style={timelineDotStyle} />
                    <div style={timelineContentStyle}>
                      <div style={timelineDateStyle}>{format(new Date(v.date), 'dd MMM yyyy')}</div>
                      <div style={timelineNoteStyle}>{v.note}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {showLog ? (
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <textarea
                  style={textareaStyle}
                  placeholder="What happened? e.g. Called patient, booked for Fri 14th..."
                  value={logNote}
                  onChange={e => setLogNote(e.target.value)}
                  autoFocus
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={handleLogVisit} style={saveLogBtn}>Save entry</button>
                  <button onClick={() => setShowLog(false)} style={cancelLogBtn}>Cancel</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setShowLog(true)} style={logVisitBtn}>
                <Plus size={13} /> Log visit or contact
              </button>
            )}
          </Section>
        </div>

        <div style={{ padding: '16px 28px', borderTop: '1px solid #E5E5E0' }}>
          <button onClick={() => onEdit(patient)} style={editBtnStyle}>Edit patient details</button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#9A9A92', fontFamily: "'JetBrains Mono', monospace", marginBottom: 14, paddingBottom: 8, borderBottom: '1px solid #E5E5E0' }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Grid2({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>{children}</div>;
}

function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#9A9A92', marginBottom: 2, fontFamily: "'JetBrains Mono', monospace" }}>{label}</div>
      <div style={{ fontSize: 13, color: '#1B2B4B', fontFamily: "'JetBrains Mono', monospace", wordBreak: 'break-word' }}>{value}</div>
    </div>
  );
}

const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' };
const panelStyle = { background: '#fff', width: 460, maxWidth: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)' };
const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '20px 28px', borderBottom: '1px solid #E5E5E0' };
const nameStyle = { fontSize: 20, fontWeight: 700, color: '#1B2B4B', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '-0.5px' };
const closeBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', color: '#7A7A72', padding: 4 };
const emptyTextStyle = { fontSize: 12, color: '#9A9A92', fontFamily: "'JetBrains Mono', monospace", fontStyle: 'italic', margin: 0 };
const timelineItemStyle = { display: 'flex', gap: 12, paddingBottom: 16, position: 'relative' };
const timelineDotStyle = { width: 8, height: 8, borderRadius: '50%', background: '#2D9B8A', marginTop: 4, flexShrink: 0 };
const timelineContentStyle = { flex: 1 };
const timelineDateStyle = { fontSize: 11, fontWeight: 700, color: '#2D9B8A', fontFamily: "'JetBrains Mono', monospace", marginBottom: 2 };
const timelineNoteStyle = { fontSize: 13, color: '#1B2B4B', fontFamily: "'JetBrains Mono', monospace", lineHeight: 1.5 };
const textareaStyle = { padding: '9px 11px', border: '1px solid #DDDDD8', borderRadius: 2, fontSize: 13, fontFamily: "'JetBrains Mono', monospace", color: '#1B2B4B', background: '#FAFAF8', resize: 'vertical', minHeight: 72, outline: 'none', width: '100%', boxSizing: 'border-box' };
const logVisitBtn = { display: 'flex', alignItems: 'center', gap: 6, marginTop: 12, padding: '8px 12px', background: 'transparent', border: '1px dashed #DDDDD8', borderRadius: 2, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#7A7A72', fontFamily: "'JetBrains Mono', monospace" };
const saveLogBtn = { padding: '9px 14px', background: '#1B2B4B', color: '#fff', border: 'none', borderRadius: 2, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" };
const cancelLogBtn = { padding: '9px 14px', background: '#fff', color: '#1B2B4B', border: '1px solid #DDDDD8', borderRadius: 2, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" };
const editBtnStyle = { width: '100%', padding: '11px 0', background: '#fff', color: '#1B2B4B', border: '1px solid #DDDDD8', borderRadius: 2, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" };
